import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import { App } from "./App";
import { ThemeProvider } from "./contexts/ThemeContext";
import { ErrorProvider } from "./contexts/ErrorContext";
import { ErrorBoundary } from "./components/layout/ErrorBoundary";
import { AuthProvider } from "./contexts/AuthContext";
import "./styles/index.css";
import { IS_STAGING } from "./utils/environment";

// Make the staging site obvious in the tab / app switcher too.
if (IS_STAGING) document.title = "Trip Tracker (Staging)";

// Initialize Sentry if a DSN is configured. Missing DSN is fine for dev.
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    tracesSampleRate: 1.0,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
    // Offline support: queue events and send when back online.
    transport: Sentry.makeBrowserOfflineTransport(Sentry.makeFetchTransport),
    transportOptions: {
      dbName: "triptracker-sentry-offline",
      storeName: "error-queue",
      maxQueueSize: 100,
      flushAtStartup: true,
    } as Sentry.BrowserOptions["transportOptions"],
  });
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <ThemeProvider>
      <AuthProvider>
        <ErrorProvider>
          <ErrorBoundary>
            <App />
          </ErrorBoundary>
        </ErrorProvider>
      </AuthProvider>
    </ThemeProvider>
  </StrictMode>
);
